<div class='container-fluid w-75'>
  <div class='list-container'>
    <h1>ExactBlue API Reference</h1>
    <p class='reference-description'>
      The ExactBlue Reference allows 3rd party developers to make REST calls with the ExactBlue water testing services.
    </p>
    <p class='reference-description'>
      Each endpoint requires a generated user ID Token to be included in the authorization headers, in the format:
    </p>
    <pre class='reference-description code-block w-75 w-md-50'>
headers: &#10100;
  Authorization: 'Bearer ' + idToken
&#10101;</pre>
    <p class='reference-description'>
      Your unique ID Token can be found by:
    </p>
    <ol>
      <li>
        Logging into your ExactBlue account <a href='https://exactblue.web.app/' target='_blank'>here</a>
      </li>
      <li>
        Open Chrome DevTools -> "Application" Tab -> "Storage" -> "IndexedDB" -> "firebaseLocalStorageDb" -> "firebaseLocalStorage"
      </li>
      <li>
        The token will be found in the stored object, in the value.stsTokenManager.accessToken property
      </li>
    </ol>

    <p class='reference-description'>
      The base URL for the API is
    </p>
    <pre
      class='reference-description code-block w-75 w-md-50'>https://us-central1-ebi-prod.cloudfunctions.net/api/v1/</pre>

  </div>

  <hr>

  <div class='list-container'>
    <h1>Zapier Connections</h1>
    <p class='reference-description'>
      Your ExactBlue data can be connected to Zapier using your ExactBlue account's UID.
    </p>
    <p class='reference-description'>
      You can find your UID and API key(s)
      <a routerLink='/api-keys' routerLinkActive='active'>here</a>
    </p>

    <p class='reference-description'>
      An API key will allow you to connect your ExactBlue data to Zapier and set up triggers for CRUD operations on test data.
    </p>
    <p class='reference-description'>
      Using an API key will allow you to access the ExactBlue API without needing your user ID token, through the Zapier platform.
    </p>
  </div>
<hr>

  <!-- Table of Contents -->
  <div class='list-container'>
    <h2>Table of Contents</h2>
    <ul>
      <!-- Simple click to scroll with implementation -->
      <li *ngFor='let header of endpointHeaders'>
        <button class='btn btn-link' (click)='scrollToElement(header)'>{{header.innerText}}</button>
      </li>
    </ul>
  </div>

  <hr>
  <!-- Users API -->

  <h2 class='list-container mb-1'>
    Users API
  </h2>

  <!-- Get User Details -->
  <div class='list-container'>
    <h2 class='endpoint-header'>Get User Details</h2>
    <p class='endpoint-description'>Returns JSON data about a single user.</p>
    <p class='endpoint-description'>{{requiresOrgAdminString}}</p>
    <ul class='endpoint-list'>
      <li>
        <h4 class='list-item'>URL</h4>
        <p class='list-item-description'>/user/:id</p>
      </li>
      <li>
        <h4 class='list-item'>Method</h4>
        <pre class='list-item-description method'>GET</pre>
      </li>
      <li>
        <h4 class='list-item'>URL Params</h4>
        <p class='list-item-description'><strong>Required:
        </strong>id: String</p>
        <p class='list-item-description'>
          This is the user's UID, found in the user's profile or the organization admin's user management page.
        </p>
      </li>
      <li>
        <h4 class='list-item'>Sample Call</h4>
        <pre class='reference-description code-block w-75 w-md-50'>
async getUserData(apiUid: string, idToken: string) &#10100;
  const response = await fetch('https://us-central1-ebi-prod.cloudfunctions.net/api/v1/user/' + apiUid, &#10100;
    method: 'GET',
    headers: &#10100;
      Authorization: 'Bearer ' + idToken
    &#10101;
  &#10101;);
  return await response.json();
&#10101;
        </pre>
      </li>
      <li>
        <h4 class='list-item'>Sample Success Response</h4>
        <p class='list-item-description'><strong>Code:</strong> 200</p>
        <strong>Content:</strong>
        <pre class='reference-description code-block w-75 w-md-50'>
&#10100;
  "id": "CrmleijCeBUxEKhZAfFL6nfCIj33",
  "data": &#10100;
    "uid": "CrmleijCeBUxEKhZAfFL6nfCIj33",
    "customTestsEnabled": false,
    "displayName": "Tom Smith",
    "active": true,
    "places": [
      &#10100;
        "formatted_address": "27, Poes Garden, Teynampet, Chennai, Tamil Nadu 600018, India",
        "lng": 80.2527693,
        "place_id": "a3e7227f-fd37-4af2-80f9-1310109441d3",
        "lat": 13.0434139,
        "name": "27, Poes Garden",
        "time_created": 1614891360
      &#10101;
    ],
    "enabledTests": [
      "h2o2",
      "chlorine",
      "bacteria"
    ],
    "orgId": "1",
    "hiddenTests": [
      "c19",
      "lead"
    ],
    "email": "email@example.com",
    "waterSources": [
      &#10100;
        "source_id": "z3h8173h-fr62-3aj6-92u8-1452637411h5",
        "place_id": "a3e7227f-fd37-4af2-80f9-1310109441d3",
        "time_created":1614898325
      &#10101;
    ]
  &#10101;
&#10101;
</pre>
      </li>
      <li>
        <h4 class='list-item'>Sample Error Responses</h4>
        <p class='list-item-description'><strong>Code:</strong> 404</p>
        <p class='list-item-description'><strong>Content:</strong></p>
        <pre class='reference-description code-block w-75 w-md-50'>
&#10100;
  error : "User doesn't exist"
&#10101;
</pre>
        <p>OR</p>
        <p class='list-item-description'><strong>Code:</strong> 401</p>
        <p class='list-item-description'>
          <strong>Content:</strong></p>
        <pre
          class='reference-description code-block w-75 w-md-50'>
&#10100;
  error : "You are unauthorized to make this request."
&#10101;
        </pre>
      </li>
    </ul>
  </div>


  <!-- Update User Details -->
  <div class='list-container'>
    <h2 class='endpoint-header'>Update User Details</h2>
    <p class='endpoint-description'>Update a user's saved data. Returns JSON data of the user's details.</p>
    <p class='endpoint-description'>{{requiresOrgAdminString}}</p>
    <p
      class='endpoint-description'>One ore more of the following optional properties must be passed into the body object:</p>
    <ul>
      <li>
        active: boolean
      </li>
      <li>
        displayName: string
      </li>
      <li>
        email: string
      </li>
      <li>
        <p>places: Array</p>
        <p>An array of one or more place objects:</p>
        <pre class='reference-description code-block w-75 w-md-50'>
&#10100;
  "formatted_address": string,
  "lat": number,
  "lng": number,
  "name": string,
  "place_id": string,
  "time_created": timestamp (seconds)
&#10101;
</pre>
      </li>
      <li>
        <p>waterSources: Array</p>
        <p>An array of one or more water source objects:</p>
        <pre class='reference-description code-block w-75 w-md-50'>
&#10100;
  "name": string,
  "place_id": string,
  "source_id": string
&#10101;
</pre>
      </li>
    </ul>
    <hr>
    <ul class='endpoint-list'>
      <li>
        <h4 class='list-item'>URL</h4>
        <p class='list-item-description'>/users/:id</p>
      </li>
      <li>
        <h4 class='list-item'>Method</h4>
        <pre class='list-item-description method'>PUT</pre>
      </li>
      <li>
        <h4 class='list-item'>URL Params</h4>
        <p class='list-item-description'><strong>Required:
        </strong>id: String</p>
        <p class='list-item-description'>
          This is the user's UID, found in the user's profile or the organization admin's user management page.
        </p>
      </li>
      <li>
        <h4 class='list-item'>Sample Call</h4>
        <pre class='reference-description code-block w-75 w-md-50'>
async updateUserDetails(uid: string, idToken: string) &#10100;
  const data = &#10100;
    "displayName": "John Smith"
  &#10101;
  await fetch('https://us-central1-ebi-prod.cloudfunctions.net/api/v1/users/' + uid, &#10100;
    method: 'PUT',
    body: JSON.stringify(data),
    headers: &#10100;
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + idToken
    &#10101;
  &#10101;);
&#10101;
</pre>
      </li>
      <li>
        <h4 class='list-item'>Sample Success Response</h4>
        <p class='list-item-description'><strong>Code:</strong> 200</p>
        <strong>Content:</strong>
        <pre class='reference-description code-block w-75 w-md-50'>
&#10100;
  "id": "CrmleijCeBUxEKhZAfFL6nfCIj33",
  "data": &#10100;
    "displayName": "John Smith"
  &#10101;
&#10101;
</pre>
      </li>
      <li>
        <h4 class='list-item'>Sample Error Responses</h4>
        <p class='list-item-description'><strong>Code:</strong> 500</p>
        <p class='list-item-description'><strong>Content:</strong></p>
        <pre class='reference-description code-block w-75 w-md-50'>
&#10100;
  "code": "auth/user-not-found",
  "message": "There is no user record corresponding to the provided identifier."
&#10101;
</pre>
        <p>OR</p>
        <p class='list-item-description'><strong>Code:</strong> 401</p>
        <p class='list-item-description'>
          <strong>Content:</strong></p>
        <pre
          class='reference-description code-block w-75 w-md-50'>
&#10100;
  error : "You are unauthorized to make this request."
&#10101;
</pre>
        <p>OR</p>
        <p class='list-item-description'><strong>Code:</strong> 401</p>
        <p class='list-item-description'>
          <strong>Content:</strong></p>
        <pre
          class='reference-description code-block w-75 w-md-50'>
&#10100;
  error : "User data is required."
&#10101;
</pre>
      </li>
    </ul>
  </div>


  <!-- Get User Custom Claims -->
  <div class='list-container'>
    <h2 class='endpoint-header'>Get a user's custom claims</h2>
    <p class='endpoint-description'>Returns JSON data of a user's Firebase authentication custom claims.</p>
    <p class='endpoint-description'>Requires the request to be from the user.</p>
    <ul class='endpoint-list'>
      <li>
        <h4 class='list-item'>URL</h4>
        <p class='list-item-description'>/user/:id/custom-claims</p>
      </li>
      <li>
        <h4 class='list-item'>Method</h4>
        <pre class='list-item-description method'>GET</pre>
      </li>
      <li>
        <h4 class='list-item'>URL Params</h4>
        <p class='list-item-description'>
          <strong>Required: </strong>
          id: String
        </p>
        <p class='list-item-description'>
          This is the user's UID, found in the user's profile or the organization admin's user management page.
        </p>
      </li>
      <li>
        <h4 class='list-item'>Sample Call</h4>
        <pre class='reference-description code-block w-75 w-md-50'>
async getUserCustomClaims(idToken: string, uid: string) &#10100;
  const response = await fetch('https://us-central1-ebi-prod.cloudfunctions.net/api/v1/user/' + uid + '/custom-claims', &#10100;
    method: 'GET',
    headers: &#10100;
      Authorization: 'Bearer ' + idToken
    &#10101;
  &#10101;);
  return await response.json();
&#10101;
</pre>
      </li>
      <li>
        <h4 class='list-item'>Sample Success Response</h4>
        <p class='list-item-description'><strong>Code:</strong> 200</p>
        <strong>Content:</strong>
        <pre class='reference-description code-block w-75 w-md-50'>
&#10100;
  "userRole": "user",
  "orgData": &#10100;
    "orgId": "3"
  &#10101;
&#10101;
</pre>
      </li>
      <li>
        <h4 class='list-item'>Sample Error Responses</h4>
        <p class='list-item-description'><strong>Code:</strong> 500</p>
        <p class='list-item-description'><strong>Content:</strong></p>
        <pre class='reference-description code-block w-75 w-md-50'>
&#10100;
  "code": "auth/user-not-found",
  "message": "There is no user record corresponding to the provided identifier."
&#10101;
</pre>
        <p>OR</p>
        <p class='list-item-description'><strong>Code:</strong> 401</p>
        <p class='list-item-description'>
          <strong>Content:</strong></p>
        <pre
          class='reference-description code-block w-75 w-md-50'>
&#10100;
  error : "You are unauthorized to make this request."
&#10101;
        </pre>
      </li>
    </ul>
  </div>


  <!-- Get All Org Users -->
  <div class='list-container'>
    <h2 class='endpoint-header'>Get all users within an organization</h2>
    <p class='endpoint-description'>Returns an array of users within an organization.</p>
    <p
      class='endpoint-description'>Requires the request to either be from the organization admin of the specified organization ID.</p>
    <ul class='endpoint-list'>
      <li>
        <h4 class='list-item'>URL</h4>
        <p class='list-item-description'>/users/:orgId</p>
      </li>
      <li>
        <h4 class='list-item'>Method</h4>
        <pre class='list-item-description method'>GET</pre>
      </li>
      <li>
        <h4 class='list-item'>URL Params</h4>
        <p class='list-item-description'><strong>Required:
        </strong>orgId: String</p>
        <p class='list-item-description'>
          This is the organization's ID, found in your user profile or the organization admin's user management page.
        </p>
      </li>
      <li>
        <h4 class='list-item'>Sample Call</h4>
        <pre class='reference-description code-block w-75 w-md-50'>
async getOrgUsers(idToken: string, orgId: string) &#10100;
  const response = await fetch('https://us-central1-ebi-prod.cloudfunctions.net/api/v1/users/' + orgId, &#10100;
    method: 'GET',
    headers: &#10100;
      Authorization: 'Bearer ' + idToken,
      'Content-Type': 'application/json'
    &#10101;
  &#10101;);
  return await response.json();
&#10101;
</pre>
      </li>
      <li>
        <h4 class='list-item'>Sample Success Response</h4>
        <p class='list-item-description'><strong>Code:</strong> 200</p>
        <strong>Content:</strong>
        <pre class='reference-description code-block w-75 w-md-50'>
[
  &#10100;
    &#10100;
      "userRecord": &#10100;
        "uid": "CrmleijCeBUxEKhZAfFL6nfCIj33",
        "email": "email@example.com",
        "emailVerified": false,
        "displayName": "John Smith",
        "disabled": false,
        "metadata": &#10100;
          "lastSignInTime": "Mon, 29 Mar 2021 14:59:02 GMT",
          "creationTime": "Wed, 20 Mar 2019 16:57:37 GMT"
        &#10101;,
        "customClaims": &#10100;
          "userRole": "admin",
          "orgData": &#10100;
            "orgId": "1"
          &#10101;
        &#10101;,
        "tokensValidAfterTime": "Wed, 20 Mar 2019 16:57:37 GMT",
        "providerData": [
          &#10100;
            "uid": "email@example.com",
            "displayName": "John Smith",
            "email": "email@example.com",
            "providerId": "password"
          &#10101;
        ]
      &#10101;,
      "userData": &#10100;
        "orgId": "1",
        "customTestsEnabled": false,
        "hiddenTests": [
          "c19",
          "lead"
        ],
        "uid": "CrmleijCeBUxEKhZAfFL6nfCIj33",
        "waterSources": [],
        "email": "email@example.com",
        "enabledTests": [
          "h2o2",
          "chlorine",
          "bacteria",
          "ecoli",
          "bacteria-100"
        ],
        "active": true,
        "places": [
          &#10100;
            "lat": 13.0434139,
            "place_id": "a3e7227f-fd37-4af2-80f9-1310109441d3",
            "formatted_address": "27, Poes Garden, Teynampet, Chennai, Tamil Nadu 600018, India",
            "name": "27, Poes Garden",
            "time_created": 1614891360,
            "lng": 80.2527693
          &#10101;,
          ...
        ],
        "displayName": "John Smith"
        &#10101;
      &#10101;,
      ...
    &#10101;
  &#10101;
]
</pre>
      </li>
      <li>
        <h4 class='list-item'>Sample Error Responses</h4>
        <p class='list-item-description'><strong>Code:</strong> 404</p>
        <p class='list-item-description'><strong>Content:</strong></p>
        <pre class='reference-description code-block w-75 w-md-50'>
&#10100;
  error : "The organization has no users or does not exist."
&#10101;
</pre>
        <p>OR</p>
        <p class='list-item-description'><strong>Code:</strong> 401</p>
        <p class='list-item-description'>
          <strong>Content:</strong></p>
        <pre
          class='reference-description code-block w-75 w-md-50'>
&#10100;
  error : "You are unauthorized to make this request."
&#10101;
        </pre>
      </li>
    </ul>
  </div>


  <!-- Tests API -->

  <h2 class='list-container mb-1'>
    Tests API
  </h2>

  <!-- Find a test -->
  <div class='list-container'>
    <h2 class='endpoint-header'>Get tests</h2>
    <p class='endpoint-description'>Find tests that match the filters passed in the body of the request.</p>
    <p class='endpoint-description'>Filters must be an object with the following optional properties:</p>
    <ul>
      <li>
        uid: string
      </li>
      <li>
        note: string
      </li>
      <li>
        water_source: string
      </li>
      <li>
        test_id: string
      </li>
      <li>
        date: number (timestamp in seconds)
      </li>
      <li>
        testTypes: string[]
      </li>
      <li>
        users: string[] (array of UIDs)
      </li>
    </ul>
    <p class='endpoint-description'>If requested by a user, will return an array of only the user's tests</p>
    <p
      class='endpoint-description'>If requested by an organization admin, will return an array of organization's tests</p>
    <hr>
    <ul class='endpoint-list'>
      <li>
        <h4 class='list-item'>URL</h4>
        <p class='list-item-description'>/tests</p>
      </li>
      <li>
        <h4 class='list-item'>Method</h4>
        <pre class='list-item-description method'>POST</pre>
      </li>
      <li>
        <h4 class='list-item'>Sample Call</h4>
        <pre class='reference-description code-block w-75 w-md-50'>
async getTestData(idToken: string) &#10100;
  const filters = &#10100;
    "water_source":"Kitchen Sink 1"
  &#10101;
  const response = await fetch('https://us-central1-ebi-prod.cloudfunctions.net/api/v1/tests', &#10100;
    method: 'POST',
    body: JSON.stringify(filters),
    headers: &#10100;
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + idToken
    &#10101;
  &#10101;);
 return await response.json();
&#10101;
</pre>
      </li>
      <li>
        <h4 class='list-item'>Sample Success Response</h4>
        <p class='list-item-description'><strong>Code:</strong> 200</p>
        <strong>Content:</strong>
        <pre class='reference-description code-block w-75 w-md-50'>
&#10100;
  "data": [
    &#10100;
      "note": "",
      "location": &#10100;
        "waterSource": "",
        "name": "123 Main St",
        "lat": 0,
        "formatted_address": "123 Main St., Toronto, ON, A1B 2C3, Canada",
        "place_id": "ChIJ-0kwkpvM1IkRfpKCns1xYYM",
        "lng": 0
      &#10101;,
      "calculationResult": &#10100;
        "textDescription": "",
        "date": &#10100;
          "_seconds": 1617029921,
          "_nanoseconds": 177000000
        &#10101;,
        "testType": "Bacteria"
      &#10101;,
      "uid": "lFAVZbIQLSNn1iHK9BqxcUjRGAz2",
      "date": &#10100;
        "_seconds": 1617029921,
        "_nanoseconds": 177000000
      &#10101;
    &#10101;,
    ...
  ]
&#10101;
</pre>
      </li>
      <li>
        <h4 class='list-item'>Sample Error Responses</h4>
        <p class='list-item-description'><strong>Code:</strong> 401</p>
        <p class='list-item-description'><strong>Content:</strong></p>
        <pre class='reference-description code-block w-75 w-md-50'>
&#10100;
  error : "Test data is required."
&#10101;
</pre>
        <p>OR</p>
        <p class='list-item-description'><strong>Code:</strong> 401</p>
        <p class='list-item-description'>
          <strong>Content:</strong></p>
        <pre
          class='reference-description code-block w-75 w-md-50'>
&#10100;
  error : "You are unauthorized to make this request."
&#10101;
        </pre>
      </li>
    </ul>
  </div>

  <!-- Update a test -->
  <div class='list-container'>
    <h2 class='endpoint-header'>Update a test</h2>
    <p class='endpoint-description'>Update a test with the specified test id. Returns JSON data of the test.</p>
    <p
      class='endpoint-description'>Requires the request to be from the test's user or the test's organization admin.</p>
    <ul class='endpoint-list'>
      <li>
        <h4 class='list-item'>URL</h4>
        <p class='list-item-description'>/tests/:id</p>
      </li>
      <li>
        <h4 class='list-item'>Method</h4>
        <pre class='list-item-description method'>PUT</pre>
      </li>
      <li>
        <h4 class='list-item'>URL Params</h4>
        <p class='list-item-description'>
          <strong>Required: </strong>
          id: String
        </p>
        <p class='list-item-description'>
          This is the test's ID, found in the test's detail page.
        </p>
      </li>
      <li>
        <h4 class='list-item'>Sample Call</h4>
        <pre class='reference-description code-block w-75 w-md-50'>
async updateTestData(testId: string, idToken: string) &#10100;
  const data = &#10100;
    "note": "Test Kitchen Sink 1 again"
  &#10101;
  await fetch('https://us-central1-ebi-prod.cloudfunctions.net/api/v1/tests/' + testId, &#10100;
    method: 'PUT',
    body: JSON.stringify(data),
    headers: &#10100;
      'Content-Type': 'application/json',
      Authorization: 'Bearer ' + idToken
    &#10101;
  &#10101;);
&#10101;
</pre>
      </li>
      <li>
        <h4 class='list-item'>Sample Success Response</h4>
        <p class='list-item-description'><strong>Code:</strong> 200</p>
        <strong>Content:</strong>
        <pre class='reference-description code-block w-75 w-md-50'>
&#10100;
  "id": "f86af9bc-ec91-42fd-824e-28f7ed3a75e9",
  "data": &#10100;
    "note": "Test Kitchen Sink 1 again"
  &#10101;
&#10101;
</pre>
      </li>
      <li>
        <h4 class='list-item'>Sample Error Responses</h4>
        <p class='list-item-description'><strong>Code:</strong> 401</p>
        <p class='list-item-description'><strong>Content:</strong></p>
        <pre class='reference-description code-block w-75 w-md-50'>
&#10100;
  error : "Test data is required."
&#10101;
</pre>
        <p>OR</p>
        <p class='list-item-description'><strong>Code:</strong> 401</p>
        <p class='list-item-description'>
          <strong>Content:</strong></p>
        <pre
          class='reference-description code-block w-75 w-md-50'>
&#10100;
  error : "You are unauthorized to make this request."
&#10101;
        </pre>
      </li>
    </ul>
  </div>

  <hr>
  <!-- Orgs API -->

  <h2 class='list-container mb-1'>
    Organizations API
  </h2>

  <!-- Get an organization's data -->
  <div class='list-container'>
    <h2 class='endpoint-header'>Get an organization's data</h2>
    <p class='endpoint-description'>Returns JSON data about a single organization</p>
    <p
      class='endpoint-description'>Requires the request to be from a user within the organization.</p>
    <ul class='endpoint-list'>
      <li>
        <h4 class='list-item'>URL</h4>
        <p class='list-item-description'>/organizations/:id</p>
      </li>
      <li>
        <h4 class='list-item'>Method</h4>
        <pre class='list-item-description method'>GET</pre>
      </li>
      <li>
        <h4 class='list-item'>URL Params</h4>
        <p class='list-item-description'>
          <strong>Required: </strong>
          id: String
        </p>
        <p class='list-item-description'>
          This is the organization's ID, found in the organization's detail page.
        </p>
      </li>
      <li>
        <h4 class='list-item'>Sample Call</h4>
        <pre class='reference-description code-block w-75 w-md-50'>
async getOrgData(idToken: string, orgId: string) &#10100;
  const res = await fetch('https://us-central1-ebi-prod.cloudfunctions.net/api/v1/organizations/' + orgId, &#10100;
    method: 'GET',
    headers: &#10100;
      Authorization: 'Bearer ' + idToken,
      'Content-Type': 'application/json'
    &#10101;
  &#10101;);
  return await res.json();
&#10101;
</pre>
      </li>
      <li>
        <h4 class='list-item'>Sample Success Response</h4>
        <p class='list-item-description'><strong>Code:</strong> 200</p>
        <strong>Content:</strong>
        <pre class='reference-description code-block w-75 w-md-50'>
&#10100;
  "hiddenTests": [
    "lead",
    "ecoli",
    "c19",
    "bacteria-100"
  ],
  "name": "Organization 1",
  "enabledTests": [
    "h2o2",
    "chlorine",
    "bacteria-100",
    "ecoli",
    "c19",
    "bacteria"
  ],
  "sharingLocations": true,
  "active": true,
  "id": "1"
&#10101;
</pre>
      </li>
      <li>
        <h4 class='list-item'>Sample Error Responses</h4>
        <p class='list-item-description'><strong>Code:</strong> 401</p>
        <p class='list-item-description'><strong>Content:</strong></p>
        <pre class='reference-description code-block w-75 w-md-50'>
&#10100;
  error : "Organization ID is required."
&#10101;
</pre>
        <p>OR</p>
        <p class='list-item-description'><strong>Code:</strong> 401</p>
        <p class='list-item-description'>
          <strong>Content:</strong></p>
        <pre
          class='reference-description code-block w-75 w-md-50'>
&#10100;
  error : "You are unauthorized to make this request."
&#10101;
        </pre>
      </li>
    </ul>
  </div>

  <!-- Update a user's organization details -->
  <div class='list-container'>
    <h2 class='endpoint-header'>Update a user's organization details</h2>
    <p class='endpoint-description'>Returns JSON data with the updated user role or organization ID.</p>
    <p
      class='endpoint-description'>Requires the request to be from a user within the organization.</p>
    <p class='endpoint-description'>Filters must be an object with the following required properties:</p>
    <ul>
      <li>
        <p>userRole: string</p>
        <p>Can be one of ["admin", "user"]</p>
      </li>
      <li>
        orgData:
        <pre class='reference-description code-block w-75 w-md-50'>
  orgData: &#10100;
    orgId: string
  &#10101;
</pre>
      </li>

    </ul>
    <hr>
    <ul class='endpoint-list'>
      <li>
        <h4 class='list-item'>URL</h4>
        <p class='list-item-description'>/organizations/:org_id/user/:uid</p>
      </li>
      <li>
        <h4 class='list-item'>Method</h4>
        <pre class='list-item-description method'>PUT</pre>
      </li>
      <li>
        <h4 class='list-item'>URL Params</h4>
        <p class='list-item-description'>
          <strong>Required: </strong>
          org_id: String
        </p>
        <p class='list-item-description'>
          This is the organization's ID, found in the organization's detail page.
        </p>
        <p class='list-item-description'>
          <strong>Required: </strong>
          uid: String
        </p>
        <p class='list-item-description'>
          This is the user's UID, found in the user's profile or the organization admin's user management page.
        </p>
      </li>
      <li>
        <h4 class='list-item'>Sample Call</h4>
        <pre class='reference-description code-block w-75 w-md-50'>
async updateUserOrgData(idToken: string, orgId: string) &#10100;
  const userRole = "admin"
  const uid = "CrmleijCeBUxEKhZAfFL6nfCIj33"
  const res = await fetch('https://us-central1-ebi-prod.cloudfunctions.net/api/v1/organizations/' + orgId + '/user/' + uid, &#10100;
    method: 'PUT',
    body: JSON.stringify(&#10100;
      userRole,
      orgData: &#10100;
        orgId
      &#10101;
    }),
    headers: &#10100;
      Authorization: 'Bearer ' + idToken,
      'Content-Type': 'application/json'
    &#10101;
  &#10101;);
  return await res.json();
&#10101;
</pre>
      </li>
      <li>
        <h4 class='list-item'>Sample Success Response</h4>
        <p class='list-item-description'><strong>Code:</strong> 200</p>
        <strong>Content:</strong>
        <pre class='reference-description code-block w-75 w-md-50'>
&#10100;
  "message": "Updated user CrmleijCeBUxEKhZAfFL6nfCIj33 with custom claims: Role: admin, orgId: 1"
&#10101;
</pre>
      </li>
      <li>
        <h4 class='list-item'>Sample Error Responses</h4>
        <p class='list-item-description'><strong>Code:</strong> 401</p>
        <p class='list-item-description'><strong>Content:</strong></p>
        <pre class='reference-description code-block w-75 w-md-50'>
&#10100;
  "code": "auth/user-not-found",
  "message": "There is no user record corresponding to the provided identifier."
&#10101;
</pre>
        <p>OR</p>
        <p class='list-item-description'><strong>Code:</strong> 401</p>
        <p class='list-item-description'>
          <strong>Content:</strong></p>
        <pre
          class='reference-description code-block w-75 w-md-50'>
&#10100;
  error : "You are unauthorized to make this request."
&#10101;
        </pre>
      </li>
    </ul>
  </div>

  <!-- Update an organization's details -->
  <div class='list-container'>
    <h2 class='endpoint-header'>Update an organization's details</h2>
    <p class='endpoint-description'>Returns JSON data with the updated organization details.</p>
    <p
      class='endpoint-description'>Requires the request to be from an organization admin.</p>
    <p class='endpoint-description'>The body must include the name property (only changeable data at the moment):</p>
    <ul>
      <li>
        name: string
      </li>
    </ul>
    <hr>
    <ul class='endpoint-list'>
      <li>
        <h4 class='list-item'>URL</h4>
        <p class='list-item-description'>/organizations/:id</p>
      </li>
      <li>
        <h4 class='list-item'>Method</h4>
        <pre class='list-item-description method'>PUT</pre>
      </li>
      <li>
        <h4 class='list-item'>URL Params</h4>
        <p class='list-item-description'>
          <strong>Required: </strong>
          id: String
        </p>
        <p class='list-item-description'>
          This is the organization's ID, found in the organization's detail page.
        </p>
      </li>
      <li>
        <h4 class='list-item'>Sample Call</h4>
        <pre class='reference-description code-block w-75 w-md-50'>
async updateOrgData(idToken: string, orgId: string) &#10100;
  const data = &#10100;
    "name":"Organization 1"
  &#10101;
  const res = await fetch('https://us-central1-ebi-prod.cloudfunctions.net/api/v1/organizations/' + orgId, &#10100;
    method: 'PUT',
    body: JSON.stringify(data),
    headers: &#10100;
      Authorization: 'Bearer ' + idToken,
      'Content-Type': 'application/json'
    &#10101;
  &#10101;);
  return await res.json();
&#10101;
</pre>
      </li>
      <li>
        <h4 class='list-item'>Sample Success Response</h4>
        <p class='list-item-description'><strong>Code:</strong> 200</p>
        <strong>Content:</strong>
        <pre class='reference-description code-block w-75 w-md-50'>
&#10100;
  "message": "Updated user CrmleijCeBUxEKhZAfFL6nfCIj33 with custom claims: Role: admin, orgId: 1"
&#10101;
</pre>
      </li>
      <li>
        <h4 class='list-item'>Sample Error Responses</h4>
        <p class='list-item-description'><strong>Code:</strong> 401</p>
        <p class='list-item-description'><strong>Content:</strong></p>
        <pre class='reference-description code-block w-75 w-md-50'>
&#10100;
  "code": "auth/user-not-found",
  "message": "There is no user record corresponding to the provided identifier."
&#10101;
</pre>
        <p>OR</p>
        <p class='list-item-description'><strong>Code:</strong> 401</p>
        <p class='list-item-description'>
          <strong>Content:</strong></p>
        <pre
          class='reference-description code-block w-75 w-md-50'>
&#10100;
  error : "You are unauthorized to make this request."
&#10101;
        </pre>
      </li>
    </ul>
  </div>

</div>
